import React, { useState } from "react";
import { Zap, Wind, Layers, Activity, ArrowRight, Info, Cpu, MoveUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EngineeredForSpeed() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const features = [{
    id: "nitro",
    title: "Nitro-Infused Foam",
    description: "Supercritical nitrogen-injected foam delivers explosive energy return with every stride, reducing fatigue by 15%.",
    specs: {
      weight: "45g",
      density: "0.12 g/cm³",
      rebound: "85%"
    },
    icon: <Zap className="w-6 h-6" />
  }, {
    id: "carbon",
    title: "Carbon-Fiber Propulsion",
    description: "Full-length rigid plate embedded within the midsole acts as a lever to propel you forward, maximizing speed efficiency.",
    specs: {
      material: "T700 Carbon",
      stiffness: "95 GPa",
      thickness: "1.2mm"
    },
    icon: <MoveUpRight className="w-6 h-6" />
  }, {
    id: "mesh",
    title: "Aerodynamic Mesh",
    description: "Single-layer monofilament upper engineered for zero drag and maximum breathability in high-heat conditions.",
    specs: {
      drag_coeff: "0.21",
      permeability: "High",
      weight: "22g"
    },
    icon: <Wind className="w-6 h-6" />
  }, {
    id: "traction",
    title: "Grip-Lock Traction",
    description: "Bio-mimetic outsole pattern inspired by gecko adhesion ensures non-slip performance on wet and dry asphalt.",
    specs: {
      friction: "0.85µ",
      durability: "High",
      compound: "Vibram-X"
    },
    icon: <Activity className="w-6 h-6" />
  }, {
    id: "stability",
    title: "Dynamic Stability Cage",
    description: "Internal skeletal structure adapts to foot swelling during long runs, maintaining lock-down without constriction.",
    specs: {
      material: "TPU",
      adaptability: "Dynamic",
      support: "Midfoot"
    },
    icon: <Layers className="w-6 h-6" />
  }, {
    id: "sensor",
    title: "Smart-Sensor Ready",
    description: "Cavity designed for StrideVault performance chips to track cadence, strike angle, and ground contact time.",
    specs: {
      compatibility: "SV-Chip v2",
      connectivity: "BLE 5.0",
      battery: "N/A"
    },
    icon: <Cpu className="w-6 h-6" />
  }];
  return <section id="engineered-for-speed" ref={ref} className={`dark bg-background text-foreground relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="flex flex-col lg:flex-row h-auto lg:h-screen">
        {}
        <div className="w-full lg:w-1/2 h-[50vh] lg:h-full relative bg-accent/5 flex items-center justify-center overflow-hidden border-b lg:border-b-0 lg:border-r border-border">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-secondary)_0%,transparent_70%)] opacity-5 blur-3xl" />
          
          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] opacity-10" />

          <div className="relative z-10 p-8 lg:p-16 w-full max-w-2xl flex flex-col items-center">
            <div className="relative w-full aspect-square max-w-md mx-auto">
               {}
               <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/sneaker_exploded_view.png" alt="Exploded view of sneaker sole technology" className="w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,255,255,0.15)] animate-pulse duration-[4000ms]" />
               
               {}
               <div className="absolute top-1/4 left-0 bg-background/80 backdrop-blur-md border border-secondary/30 px-3 py-1 rounded-full text-[10px] font-mono text-secondary shadow-[0_0_15px_rgba(0,255,255,0.2)] hidden sm:block">
                  FOAM DENSITY: 0.12
               </div>
               <div className="absolute bottom-1/3 right-0 bg-background/80 backdrop-blur-md border border-primary/30 px-3 py-1 rounded-full text-[10px] font-mono text-primary shadow-[0_0_15px_rgba(190,255,0,0.2)] hidden sm:block">
                  PROPULSION: ACTIVE
               </div>
            </div>
            
            <div className="mt-8 text-center lg:text-left w-full">
              <h3 className="text-2xl font-bold tracking-tighter uppercase text-foreground mb-2">
                Core Architecture
              </h3>
              <p className="text-muted-foreground text-sm max-w-md mx-auto lg:mx-0">
                Deconstructed view of the SV-X1 platform. Every layer is calibrated for maximum energy return and minimal weight.
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="w-full lg:w-1/2 h-auto lg:h-full bg-background relative">
          <ScrollArea className="h-full w-full">
            <div className="p-8 lg:p-20 max-w-3xl mx-auto flex flex-col gap-16 lg:gap-32 min-h-full">
              
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-mono uppercase tracking-widest">
                  <Activity className="w-3 h-3" />
                  Lab Tested Performance
                </div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tighter leading-[0.9]">
                  ENGINEERED <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                    FOR SPEED
                  </span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                  Our research lab spent 18 months perfecting the balance between weight, cushion, and responsiveness. The result is a shoe that disappears on your foot.
                </p>
              </div>

              <div className="space-y-12">
                {features.map((feature, i) => <div key={feature.id} data-index={i} className="group relative pl-8 border-l border-border hover:border-secondary transition-colors duration-300">
                    <div className="absolute -left-[5px] top-0 w-2.5 h-2.5 rounded-full bg-background border border-border group-hover:border-secondary group-hover:bg-secondary transition-all duration-300" />
                    
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                            {feature.icon}
                          </div>
                          <h3 className="text-xl font-bold text-foreground group-hover:text-secondary transition-colors">
                            {feature.title}
                          </h3>
                        </div>
                        
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-secondary">
                              <Info className="w-4 h-4" />
                              <span className="sr-only">Tech Specs</span>
                            </Button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-64 bg-card border-border text-card-foreground backdrop-blur-xl">
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold text-secondary">Technical Specifications</h4>
                              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                                {Object.entries(feature.specs).map(([key, value]) => <React.Fragment key={key}>
                                    <span className="text-muted-foreground uppercase">{key}:</span>
                                    <span className="text-foreground text-right">{value}</span>
                                  </React.Fragment>)}
                              </div>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="pt-12 border-t border-border">
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div>
                    <h4 className="text-lg font-bold text-foreground">Ready to fly?</h4>
                    <p className="text-sm text-muted-foreground">Experience the future of running today.</p>
                  </div>
                  <Link to="/performance-lab">
                    <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold">
                      Explore The Lab <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

            </div>
          </ScrollArea>
        </div>
      </div>
    </section>;
}